import React from "react";
import { Link } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  requestAnonymousReset,
  requestAnonymousResetSms,
  fetchResetOptions,
  type ResetOptions,
} from "@/lib/api";
import { useT } from "@/app/localization";

type Channel = "email" | "sms";

/** A legal link when configured, else a clearly-marked placeholder (never an
 *  invented URL). */
function LegalLink({ label, url }: { label: string; url: string }) {
  const t = useT();
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 underline hover:opacity-80">
        {label}
      </a>
    );
  }
  return <span className="opacity-60">{t("operator.auth.legal_link_not_configured", { label })}</span>;
}

export function ForgotPasswordPage() {
  const t = useT();
  const [options, setOptions] = React.useState<ResetOptions | null>(null);
  const [channel, setChannel] = React.useState<Channel>("email");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [consent, setConsent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  React.useEffect(() => {
    // Backend-authoritative: the SMS option only appears when the channel is on.
    fetchResetOptions()
      .then(setOptions)
      .catch(() =>
        setOptions({
          email_enabled: true,
          sms_enabled: false,
          sms_consent_text: "",
          sms_consent_version: "",
          privacy_policy_url: "",
          terms_of_service_url: "",
        }),
      );
  }, []);

  const smsOn = Boolean(options?.sms_enabled);
  const canSubmit =
    channel === "email" ? Boolean(email.trim()) : Boolean(phone.trim()) && consent;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading || !canSubmit) return;
    setLoading(true);
    try {
      if (channel === "email") await requestAnonymousReset(email.trim());
      else await requestAnonymousResetSms(phone.trim(), consent);
    } catch {
      // Deliberately ignored: surfacing a failure would leak signal and differ
      // from the success path. The confirmation is always the same.
    } finally {
      setLoading(false);
      setSent(true);
    }
  }

  const title = sent
    ? channel === "email"
      ? t("operator.auth.forgot_check_email")
      : t("operator.auth.forgot_check_phone")
    : t("operator.auth.reset_title");

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            {!sent && (
              <CardDescription>
                {smsOn
                  ? t("operator.auth.forgot_choose_channel")
                  : t("operator.auth.forgot_email_description")}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4">
                {/* The backend answers the same generic 200 whether or not the identifier
                    matches an account, so this is ONE neutral confirmation per channel —
                    it never reveals whether an account exists. */}
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                  {channel === "email" ? t("operator.auth.forgot_sent_email") : t("operator.auth.forgot_sent_sms")}
                </p>
                <Button asChild className="w-full" variant="outline">
                  <Link to="/login">{t("operator.auth.back_to_sign_in")}</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                {smsOn && (
                  <div className="flex rounded-lg border p-1 text-sm">
                    <button
                      type="button"
                      onClick={() => setChannel("email")}
                      className={`flex-1 rounded-md py-1.5 ${channel === "email" ? "bg-zinc-200 font-medium dark:bg-zinc-700" : ""}`}
                    >
                      {t("operator.auth.channel_email")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setChannel("sms")}
                      className={`flex-1 rounded-md py-1.5 ${channel === "sms" ? "bg-zinc-200 font-medium dark:bg-zinc-700" : ""}`}
                    >
                      {t("operator.auth.channel_sms")}
                    </button>
                  </div>
                )}

                {channel === "email" ? (
                  <div>
                    <Label>{t("operator.auth.email")}</Label>
                    <Input
                      className="mt-2"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoFocus
                      autoComplete="email"
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Label>{t("operator.auth.mobile_number")}</Label>
                      <Input
                        className="mt-2"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        autoFocus
                        autoComplete="tel"
                        placeholder={t("operator.auth.mobile_number_placeholder")}
                      />
                    </div>
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                      />
                      {/* Server consent text, rendered exactly as sent: its SHA-256 is the consent evidence (D-7). */}
                      <span className="text-xs leading-relaxed text-zinc-600 whitespace-pre-line dark:text-zinc-300">
                        {options?.sms_consent_text}
                      </span>
                    </label>
                    <div className="text-center text-xs">
                      <LegalLink label={t("operator.auth.privacy_policy")} url={options?.privacy_policy_url || ""} />
                      <span className="px-2 opacity-40">|</span>
                      <LegalLink label={t("operator.auth.terms_of_service")} url={options?.terms_of_service_url || ""} />
                    </div>
                  </div>
                )}

                <Button className="w-full" type="submit" disabled={loading || !canSubmit}>
                  {loading
                    ? t("operator.auth.sending")
                    : channel === "email"
                      ? t("operator.auth.forgot_send_link")
                      : t("operator.auth.forgot_send_code")}
                </Button>
                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                  >
                    {t("operator.auth.back_to_sign_in")}
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
